import type { ImageLoaderProps } from 'next/image';
import {
  buildFederatedPhotoTreatmentUrl,
  buildPresetTreatmentName,
  type CcFtSize,
  extractFederatedPhotoInfoFromUrl,
  FED_PHOTO_WIDTHS,
  type FederatedPhotoTreatment,
  isCustomFederatedPhotoTreatment,
} from '@/src/components/FederatedPhoto/federatedPhoto';

/**
 * next/image custom loader — keeps the browser pointed at Federated Photos
 * for each device size next/image asks for, so we get a real Federated Photo
 * registered treatment srcset without routing dynamic listing images through
 * Next.js image optimization (which would route through the Sharp pipeline).
 *
 * Treatment hints come in via a `fp_treatment` query param the
 * `<FederatedPhoto>` wrapper appends to the src for any treatment other
 * than the default `cc_ft`. Encoding via the URL (rather than a per-image
 * loader function prop) keeps the wrapper RSC-compatible — function props
 * can't cross the server/client boundary, but a string query param can.
 *
 * Wired in `next.config.ts` via `images.loader: 'custom'` +
 * `images.loaderFile`, alongside `deviceSizes: [384, 576, 768, 960, 1152, 1536]` so
 * next/image only requests widths Federated Photo already publishes.
 */

const TREATMENT_PARAM = 'fp_treatment';
const CUSTOM_PREFIX = 'custom:';

function snapToCcFtSize(width: number): CcFtSize {
  for (const size of FED_PHOTO_WIDTHS) {
    if (size >= width) return size;
  }
  return FED_PHOTO_WIDTHS[FED_PHOTO_WIDTHS.length - 1];
}

function decodeTreatment(value: string | null): FederatedPhotoTreatment {
  if (!value || value === 'cc_ft') return 'cc_ft';
  if (value.startsWith(CUSTOM_PREFIX)) {
    try {
      const json = decodeURIComponent(value.slice(CUSTOM_PREFIX.length));
      const parsed = JSON.parse(json);
      if (parsed && typeof parsed.defaultTreatment === 'string') {
        return parsed as FederatedPhotoTreatment;
      }
    } catch {
      // Malformed hint — fall through to default treatment.
    }
  }
  return 'cc_ft';
}

export default function federatedPhotoLoader({ src, width }: ImageLoaderProps): string {
  let cleanSrc = src;
  let treatment: FederatedPhotoTreatment = 'cc_ft';
  try {
    const url = new URL(src);
    treatment = decodeTreatment(url.searchParams.get(TREATMENT_PARAM));
    url.searchParams.delete(TREATMENT_PARAM);
    cleanSrc = url.toString();
  } catch {
    return src;
  }

  const info = extractFederatedPhotoInfoFromUrl(cleanSrc);
  if (!info) return cleanSrc;

  if (isCustomFederatedPhotoTreatment(treatment)) {
    const variants = treatment.srcSetTreatments ?? [];
    const variant = variants.find((v) => v.width >= width) ?? variants[variants.length - 1];
    const treatmentName = variant?.treatment ?? treatment.defaultTreatment;
    return buildFederatedPhotoTreatmentUrl(info.photoKey, treatmentName, { origin: info.origin });
  }

  const snapped = snapToCcFtSize(width);
  const treatmentName = buildPresetTreatmentName(treatment, snapped);
  return buildFederatedPhotoTreatmentUrl(info.photoKey, treatmentName, { origin: info.origin });
}
